import {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

import { LatLng } from 'leaflet';

import { Dayjs } from 'dayjs';

import P4RReducer, {
  initialState,
  ChangeCurrentTimePayload,
  ChangeEventLibraryFilters,
  TimelineType,
  P4RContextType,
} from './P4RReducer';

import {
  CHANGE_LICENCE_AREA_SELECTED,
  CHANGE_LAYER_SELECTED,
  CHANGE_EVENT_WIND_PARTICULES_SELECTED,
  CHANGE_WIND_PARTICULES_SELECTED,
  CHANGE_GRID_LAYER_SELECTED,
  CHANGE_EVENT_GRID_LAYER_SELECTED,
  CHANGE_CURRENT_TIME,
  CHANGE_TIMELINE_PLAYING,
  CHANGE_EVENT_LIBRARY_PANEL,
  CHANGE_EVENT_LIBRARY_FILTERS,
  CHANGE_MARKER_COORDINATES,
  CHANGE_WEATHER_HORODATETIME,
  CHANGE_CURRENT_TIME_COMPARISON,
  CHANGE_STARTING_TIME,
  CHANGE_START_DATE,
  CHANGE_TOTAL_KPI_LINE_CHART,
  CHANGE_MODAL_IS_OPEN,
  CHANGE_EVENT_NAME,
  CHANGE_EVENT_LAYER_SELECTED,
  CHANGE_EVENT_LICENCE_AREA_SELECTED,
  CHANGE_VELOCITY_LAYER_TIMING,
  CHANGE_TIMELINE_MODE,
  CHANGE_SUBSTATIONS_SELECTED,
  CHANGE_DISTRICT_SUBSTATIONS_SELECTED,
  CHANGE_EVENT_SUBSTATIONS_SELECTED,
} from './constants';

const AppContext = createContext<P4RContextType>(initialState);

export function P4RContextProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(P4RReducer, initialState);

  const changeVelocityLayerTiming = useCallback((payload: number) => {
    dispatch({
      type: CHANGE_VELOCITY_LAYER_TIMING,
      payload,
    });
  }, []);

  const changeLicenceAreaSelected = useCallback((payload: string | boolean) => {
    dispatch({
      type: CHANGE_LICENCE_AREA_SELECTED,
      payload,
    });
  }, []);

  const changeLayerSelected = useCallback((payload: string) => {
    dispatch({
      type: CHANGE_LAYER_SELECTED,
      payload,
    });
  }, []);

  const changeWindParticulesSelected = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_WIND_PARTICULES_SELECTED,
      payload,
    });
  }, []);

  const changeEventWindParticulesSelected = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_EVENT_WIND_PARTICULES_SELECTED,
      payload,
    });
  }, []);

  const changeGridLayerSelected = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_GRID_LAYER_SELECTED,
      payload,
    });
  }, []);

  const changeEventGridLayerSelected = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_EVENT_GRID_LAYER_SELECTED,
      payload,
    });
  }, []);

  const changeEventLicenceAreaSelected = useCallback(
    (payload: string | boolean) => {
      dispatch({
        type: CHANGE_EVENT_LICENCE_AREA_SELECTED,
        payload,
      });
    },
    [],
  );

  const changeEventLayerSelected = useCallback((payload: string) => {
    dispatch({
      type: CHANGE_EVENT_LAYER_SELECTED,
      payload,
    });
  }, []);

  const changeStartDate = useCallback(
    (payload: [Dayjs, string, string, string]) => {
      dispatch({
        type: CHANGE_START_DATE,
        payload,
      });
    },
    [],
  );

  const changeCurrentTime = useCallback((payload: ChangeCurrentTimePayload) => {
    dispatch({
      type: CHANGE_CURRENT_TIME,
      payload,
    });
  }, []);

  const changeStartingTime = useCallback(
    (payload: { currentTime: TimelineType; nextTime: TimelineType }) => {
      dispatch({
        type: CHANGE_STARTING_TIME,
        payload,
      });
    },
    [],
  );

  const changeCurrentTimeComparison = useCallback(
    (payload: ChangeCurrentTimePayload) => {
      dispatch({
        type: CHANGE_CURRENT_TIME_COMPARISON,
        payload,
      });
    },
    [],
  );

  const changeTimelinePlaying = useCallback((payload: boolean | null) => {
    dispatch({
      type: CHANGE_TIMELINE_PLAYING,
      payload,
    });
  }, []);

  const changeWeatherHorodatetime = useCallback((payload: string) => {
    dispatch({
      type: CHANGE_WEATHER_HORODATETIME,
      payload,
    });
  }, []);

  const changeMarkerCoordinates = useCallback((payload: LatLng | null) => {
    dispatch({
      type: CHANGE_MARKER_COORDINATES,
      payload,
    });
  }, []);

  const changeEventLibraryPanel = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_EVENT_LIBRARY_PANEL,
      payload,
    });
  }, []);

  const changeEventLibraryFilters = useCallback(
    (payload: ChangeEventLibraryFilters) => {
      dispatch({
        type: CHANGE_EVENT_LIBRARY_FILTERS,
        payload,
      });
    },
    [],
  );

  const changeTotalKpiForLineChart = useCallback((payload: any) => {
    dispatch({
      type: CHANGE_TOTAL_KPI_LINE_CHART,
      payload,
    });
  }, []);

  const changeModalIsOpen = useCallback((payload: boolean) => {
    dispatch({
      type: CHANGE_MODAL_IS_OPEN,
      payload,
    });
  }, []);

  const changeEventName = useCallback((payload: string) => {
    dispatch({
      type: CHANGE_EVENT_NAME,
      payload,
    });
  }, []);

  const changeTimelineMode = useCallback((payload: 'live' | 'replay' | 'historical') => {
    dispatch({
      type: CHANGE_TIMELINE_MODE,
      payload,
    });
  }, []);

  const changeSubstationsSelected = useCallback((payload: Set<string>) => {
    dispatch({
      type: CHANGE_SUBSTATIONS_SELECTED,
      payload,
    });
  }, []);

  const changeDistrictSubstationsSelected = useCallback((payload: Set<string>) => {
    dispatch({
      type: CHANGE_DISTRICT_SUBSTATIONS_SELECTED,
      payload,
    });
  }, []);

  const changeEventSubstationsSelected = useCallback((payload: Set<string>) => {
    dispatch({
      type: CHANGE_EVENT_SUBSTATIONS_SELECTED,
      payload,
    });
  }, []);

  const value = useMemo(
    () => ({
      velocityLayerTiming: state.velocityLayerTiming,
      changeVelocityLayerTiming,
      licenceAreaSelected: state.licenceAreaSelected,
      changeLicenceAreaSelected,
      layerSelected: state.layerSelected,
      changeLayerSelected,
      windParticulesSelected: state.windParticulesSelected,
      changeWindParticulesSelected,
      eventWindParticulesSelected: state.eventWindParticulesSelected,
      changeEventWindParticulesSelected,
      gridLayerSelected: state.gridLayerSelected,
      changeGridLayerSelected,
      eventGridLayerSelected: state.eventGridLayerSelected,
      changeEventGridLayerSelected,
      eventLicenceAreaSelected: state.eventLicenceAreaSelected,
      changeEventLicenceAreaSelected,
      eventLayerSelected: state.eventLayerSelected,
      changeEventLayerSelected,
      startDate: state.startDate,
      changeStartDate,
      currentTime: state.currentTime,
      nextTime: state.nextTime,
      changeCurrentTime,
      startingTime: state.startingTime,
      changeStartingTime,
      currentTimeComparison: state.currentTimeComparison,
      nextTimeComparison: state.nextTimeComparison,
      changeCurrentTimeComparison,
      timelinePlaying: state.timelinePlaying,
      changeTimelinePlaying,
      weatherHorodatetime: state.weatherHorodatetime,
      changeWeatherHorodatetime,
      markerCoordinates: state.markerCoordinates,
      changeMarkerCoordinates,
      eventLibraryPanel: state.eventLibraryPanel,
      changeEventLibraryPanel,
      eventLibraryFilters: state.eventLibraryFilters,
      changeEventLibraryFilters,
      totalKpiforLineChart: state.totalKpiforLineChart,
      changeTotalKpiForLineChart,
      isModalOpen: state.isModalOpen,
      changeModalIsOpen,
      eventName: state.eventName,
      changeEventName,
      timelineMode: state.timelineMode,
      changeTimelineMode,
      substationsSelected: state.substationsSelected,
      changeSubstationsSelected,
      districtSubstationsSelected: state.districtSubstationsSelected,
      changeDistrictSubstationsSelected,
      eventSubstationsSelected: state.eventSubstationsSelected,
      changeEventSubstationsSelected,
    }),
    [
      state.velocityLayerTiming,
      changeVelocityLayerTiming,
      state.licenceAreaSelected,
      changeLicenceAreaSelected,
      state.layerSelected,
      changeLayerSelected,
      state.windParticulesSelected,
      changeWindParticulesSelected,
      state.eventWindParticulesSelected,
      changeEventWindParticulesSelected,
      state.gridLayerSelected,
      changeGridLayerSelected,
      state.eventGridLayerSelected,
      changeEventGridLayerSelected,
      state.eventLicenceAreaSelected,
      changeEventLicenceAreaSelected,
      state.eventLayerSelected,
      changeEventLayerSelected,
      state.startDate,
      changeStartDate,
      state.currentTime,
      state.nextTime,
      changeCurrentTime,
      state.startingTime,
      changeStartingTime,
      state.currentTimeComparison,
      state.nextTimeComparison,
      changeCurrentTimeComparison,
      state.timelinePlaying,
      changeTimelinePlaying,
      state.weatherHorodatetime,
      changeWeatherHorodatetime,
      state.markerCoordinates,
      changeMarkerCoordinates,
      state.eventLibraryPanel,
      changeEventLibraryPanel,
      state.eventLibraryFilters,
      changeEventLibraryFilters,
      state.totalKpiforLineChart,
      changeTotalKpiForLineChart,
      state.isModalOpen,
      changeModalIsOpen,
      state.eventName,
      changeEventName,
      state.timelineMode,
      changeTimelineMode,
      state.substationsSelected,
      changeSubstationsSelected,
      state.districtSubstationsSelected,
      changeDistrictSubstationsSelected,
      state.eventSubstationsSelected,
      changeEventSubstationsSelected,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export default function useP4RContext() {
  return useContext(AppContext);
}
